import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Pencil, Trash2, Plus, LogOut, Upload, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emptyProduct = {
  slug: "", name_es: "", name_en: "", desc_es: "", desc_en: "", category: "hogar",
  subcategory: "", material: "rPET reciclado", colors: [], price_cop: "", price_usd: "",
  image: "", featured: false, active: true, purchasable: true,
};

export default function Admin() {
  const [password, setPassword] = useState(sessionStorage.getItem("admin_pw") || "");
  const [authed, setAuthed] = useState(false);
  const [pwInput, setPwInput] = useState("");
  const [products, setProducts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ bottles_recycled: 0, products_created: 0 });
  const [editing, setEditing] = useState(null);
  const [editingReview, setEditingReview] = useState(null);
  const [payLinks, setPayLinks] = useState([]);
  const [creations, setCreations] = useState([]);
  const [linkForm, setLinkForm] = useState({ concept: "", amount: "", currency: "cop" });
  const [creatingLink, setCreatingLink] = useState(false);
  const [uploading, setUploading] = useState(false);
  const imgFileRef = useRef(null);

  const hdr = useCallback(() => ({ headers: { "X-Admin-Password": password } }), [password]);

  const loadAll = useCallback(async (pw) => {
    const h = { headers: { "X-Admin-Password": pw } };
    const [p, r, o, s, rv, pl, cr] = await Promise.all([
      axios.get(`${API}/admin/products`, h),
      axios.get(`${API}/admin/design-requests`, h),
      axios.get(`${API}/admin/orders`, h),
      axios.get(`${API}/stats`),
      axios.get(`${API}/admin/reviews`, h),
      axios.get(`${API}/admin/payment-links`, h),
      axios.get(`${API}/creations?limit=200`),
    ]);
    setProducts(p.data); setRequests(r.data); setOrders(o.data); setStats(s.data); setReviews(rv.data); setPayLinks(pl.data); setCreations(cr.data);
  }, []);

  useEffect(() => {
    if (password) {
      loadAll(password).then(() => setAuthed(true)).catch(() => sessionStorage.removeItem("admin_pw"));
    }
  }, [password, loadAll]);

  const login = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { password: pwInput });
      sessionStorage.setItem("admin_pw", pwInput);
      setPassword(pwInput);
    } catch {
      toast.error("Contraseña incorrecta");
    }
  };

  const saveProduct = async () => {
    const body = {
      ...editing,
      price_cop: editing.price_cop === "" || editing.price_cop == null ? null : Number(editing.price_cop),
      price_usd: editing.price_usd === "" || editing.price_usd == null ? null : Number(editing.price_usd),
      colors: Array.isArray(editing.colors) ? editing.colors : String(editing.colors).split(",").map((c) => c.trim()).filter(Boolean),
      subcategory: editing.subcategory || null,
    };
    try {
      if (editing.id) {
        const { id, ...update } = body;
        await axios.put(`${API}/admin/products/${id}`, update, hdr());
      } else {
        if (!body.slug) body.slug = body.name_es.toLowerCase().replace(/\s+/g, "_").slice(0, 40);
        await axios.post(`${API}/admin/products`, body, hdr());
      }
      toast.success("Producto guardado");
      setEditing(null);
      loadAll(password);
    } catch {
      toast.error("Error al guardar");
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    await axios.delete(`${API}/admin/products/${id}`, hdr());
    toast.success("Producto eliminado");
    loadAll(password);
  };

  const saveReview = async () => {
    try {
      if (editingReview.id) {
        const { id, ...update } = editingReview;
        await axios.put(`${API}/admin/reviews/${id}`, update, hdr());
      } else {
        await axios.post(`${API}/admin/reviews`, editingReview, hdr());
      }
      toast.success("Reseña guardada");
      setEditingReview(null);
      loadAll(password);
    } catch {
      toast.error("Error al guardar");
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("¿Eliminar esta reseña?")) return;
    await axios.delete(`${API}/admin/reviews/${id}`, hdr());
    toast.success("Reseña eliminada");
    loadAll(password);
  };

  const createPayLink = async () => {
    if (!linkForm.concept || !linkForm.amount) { toast.error("Completa concepto y monto"); return; }
    setCreatingLink(true);
    try {
      await axios.post(`${API}/admin/payment-links`, {
        concept: linkForm.concept, amount: Number(linkForm.amount), currency: linkForm.currency,
      }, hdr());
      toast.success("Link de pago creado");
      setLinkForm({ concept: "", amount: "", currency: "cop" });
      loadAll(password);
    } catch {
      toast.error("Error al crear el link");
    } finally {
      setCreatingLink(false);
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload-image`, fd, hdr());
      setEditing((prev) => ({ ...prev, image: data.url }));
      toast.success("Foto subida");
    } catch {
      toast.error("Error al subir la foto");
    } finally {
      setUploading(false);
    }
  };

  const saveStats = async () => {
    await axios.put(`${API}/admin/stats`, {
      bottles_recycled: Number(stats.bottles_recycled), products_created: Number(stats.products_created),
    }, hdr());
    toast.success("Contadores actualizados");
  };

  if (!authed) {
    return (
      <div data-testid="admin-login-page" className="pt-16 min-h-screen flex items-center justify-center px-5">
        <form onSubmit={login} className="w-full max-w-sm space-y-4">
          <h1 className="font-serif-d text-3xl font-semibold text-[#132A1D]">Panel de administración</h1>
          <Input data-testid="admin-password-input" type="password" placeholder="Contraseña"
            value={pwInput} onChange={(e) => setPwInput(e.target.value)}
            className="bg-white border-black/10 h-11 rounded-xl" />
          <Button data-testid="admin-login-btn" type="submit"
            className="w-full rounded-full bg-[#2E5A3B] hover:bg-[#C86A4B] text-white h-11 transition-colors duration-300">
            Entrar
          </Button>
        </form>
      </div>
    );
  }

  const ef = (k, label, type = "text") => (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold">{label}</Label>
      <Input data-testid={`admin-field-${k}`} type={type} value={editing[k] ?? ""}
        onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
        className="bg-white border-black/10 h-10 rounded-lg" />
    </div>
  );

  return (
    <div data-testid="admin-page" className="pt-16 min-h-screen">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-12">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-serif-d text-4xl font-semibold text-[#132A1D]">Administración</h1>
          <Button variant="ghost" data-testid="admin-logout-btn" onClick={() => { sessionStorage.removeItem("admin_pw"); setAuthed(false); setPassword(""); }}>
            <LogOut size={16} className="mr-2" /> Salir
          </Button>
        </div>

        <Tabs defaultValue="productos">
          <TabsList className="mb-6">
            <TabsTrigger value="productos" data-testid="admin-tab-productos">Productos</TabsTrigger>
            <TabsTrigger value="solicitudes" data-testid="admin-tab-solicitudes">Solicitudes ({requests.length})</TabsTrigger>
            <TabsTrigger value="pedidos" data-testid="admin-tab-pedidos">Pedidos</TabsTrigger>
            <TabsTrigger value="resenas" data-testid="admin-tab-resenas">Reseñas</TabsTrigger>
            <TabsTrigger value="links" data-testid="admin-tab-links">Links de pago</TabsTrigger>
            <TabsTrigger value="contadores" data-testid="admin-tab-contadores">Contadores</TabsTrigger>
            <TabsTrigger value="galeria" data-testid="admin-tab-galeria">Galería ({creations.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="productos">
            <Button data-testid="admin-new-product-btn" onClick={() => setEditing({ ...emptyProduct })}
              className="rounded-full bg-[#C86A4B] hover:bg-[#2E5A3B] text-white mb-6 transition-colors duration-300">
              <Plus size={16} className="mr-2" /> Nuevo producto
            </Button>
            <div className="space-y-2">
              {products.map((p) => (
                <div key={p.id} data-testid={`admin-product-row-${p.slug}`}
                  className="flex items-center gap-4 bg-white rounded-xl p-3 border border-black/5">
                  <img src={p.image} alt="" className="w-14 h-14 rounded-lg object-cover bg-[#EFECE6]" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{p.name_es} {!p.active && <span className="text-xs text-red-500">(inactivo)</span>}</p>
                    <p className="text-xs text-[#5C5C5C]">{p.category}{p.subcategory ? ` · ${p.subcategory}` : ""} · {p.price_cop ? `$${p.price_cop.toLocaleString("es-CO")} COP` : "Sin precio"}</p>
                  </div>
                  <button data-testid={`admin-edit-${p.slug}`} onClick={() => setEditing({ ...p, colors: p.colors?.join(", ") })}
                    className="p-2 hover:text-[#2E5A3B]"><Pencil size={16} /></button>
                  <button data-testid={`admin-delete-${p.slug}`} onClick={() => deleteProduct(p.id)}
                    className="p-2 hover:text-red-600"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="solicitudes">
            <div className="space-y-3">
              {requests.length === 0 && <p className="text-sm text-[#5C5C5C]">Sin solicitudes todavía.</p>}
              {requests.map((r) => (
                <div key={r.id} className="bg-white rounded-xl p-4 border border-black/5 text-sm">
                  <div className="flex flex-wrap justify-between gap-2 mb-2">
                    <p className="font-bold">{r.name} · {r.city || "—"}</p>
                    <p className="text-xs text-[#5C5C5C]">{new Date(r.created_at).toLocaleString()}</p>
                  </div>
                  <p className="text-[#5C5C5C] mb-2">{r.description}</p>
                  <p className="text-xs">📐 {r.dimensions || "—"} · 🎨 {r.color || "—"} · x{r.quantity} · 📅 {r.needed_by || "—"}</p>
                  <p className="text-xs mt-1">✉️ {r.email} · 📱 {r.phone}</p>
                  {r.files?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {r.files.map((f, i) => (
                        <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${f.stored}`} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-[#2E5A3B] underline">{f.original}</a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="pedidos">
            <div className="space-y-2">
              {orders.length === 0 && <p className="text-sm text-[#5C5C5C]">Sin pedidos todavía.</p>}
              {orders.map((o) => (
                <div key={o.session_id} className="flex flex-wrap items-center justify-between gap-2 bg-white rounded-xl p-3 border border-black/5 text-sm">
                  <div>
                    <p className="font-semibold">{o.product_name} x{o.quantity}</p>
                    <p className="text-xs text-[#5C5C5C]">{new Date(o.created_at).toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{(o.amount / 100).toLocaleString(o.currency === "cop" ? "es-CO" : "en-US")} {o.currency?.toUpperCase()}</p>
                    <p className={`text-xs font-semibold ${o.payment_status === "paid" ? "text-[#2E5A3B]" : "text-[#C86A4B]"}`}>{o.payment_status}</p>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="resenas">
            <Button data-testid="admin-new-review-btn"
              onClick={() => setEditingReview({ name: "", handle: "", text_es: "", text_en: "", image: "", active: true })}
              className="rounded-full bg-[#C86A4B] hover:bg-[#2E5A3B] text-white mb-6 transition-colors duration-300">
              <Plus size={16} className="mr-2" /> Nueva reseña
            </Button>
            <div className="space-y-2">
              {reviews.map((r) => (
                <div key={r.id} className="flex items-start gap-4 bg-white rounded-xl p-3 border border-black/5">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{r.name} <span className="text-xs text-[#C86A4B]">{r.handle}</span> {!r.active && <span className="text-xs text-red-500">(oculta)</span>}</p>
                    <p className="text-xs text-[#5C5C5C] mt-1">{r.text_es}</p>
                  </div>
                  <button data-testid={`admin-edit-review-${r.id}`} onClick={() => setEditingReview({ ...r })} className="p-2 hover:text-[#2E5A3B]"><Pencil size={16} /></button>
                  <button data-testid={`admin-delete-review-${r.id}`} onClick={() => deleteReview(r.id)} className="p-2 hover:text-red-600"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="links">
            <div className="bg-white rounded-2xl p-5 border border-black/5 mb-6 max-w-2xl">
              <p className="font-semibold text-sm mb-1">Generar link de pago para un encargo cotizado</p>
              <p className="text-xs text-[#5C5C5C] mb-4">Crea un link de Stripe y envíaselo al cliente por WhatsApp o correo. El pago llega directo a tu cuenta.</p>
              <div className="grid sm:grid-cols-[1fr_140px_100px_auto] gap-3 items-end">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Concepto</Label>
                  <Input data-testid="paylink-concept" placeholder="Ej: Figura personalizada de perro" value={linkForm.concept}
                    onChange={(e) => setLinkForm({ ...linkForm, concept: e.target.value })}
                    className="bg-white border-black/10 h-10 rounded-lg" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Monto</Label>
                  <Input data-testid="paylink-amount" type="number" placeholder="80000" value={linkForm.amount}
                    onChange={(e) => setLinkForm({ ...linkForm, amount: e.target.value })}
                    className="bg-white border-black/10 h-10 rounded-lg" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Moneda</Label>
                  <select data-testid="paylink-currency" value={linkForm.currency}
                    onChange={(e) => setLinkForm({ ...linkForm, currency: e.target.value })}
                    className="w-full bg-white border border-black/10 h-10 rounded-lg px-2 text-sm">
                    <option value="cop">COP</option>
                    <option value="usd">USD</option>
                  </select>
                </div>
                <Button data-testid="paylink-create-btn" onClick={createPayLink} disabled={creatingLink}
                  className="rounded-full bg-[#C86A4B] hover:bg-[#2E5A3B] text-white h-10 px-6 transition-colors duration-300">
                  {creatingLink ? "Creando..." : "Crear link"}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              {payLinks.length === 0 && <p className="text-sm text-[#5C5C5C]">Sin links todavía.</p>}
              {payLinks.map((l) => (
                <div key={l.id} data-testid={`paylink-row-${l.id}`} className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-xl p-3 border border-black/5 text-sm">
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{l.concept}</p>
                    <p className="text-xs text-[#5C5C5C]">{new Date(l.created_at).toLocaleString()} · {l.amount.toLocaleString(l.currency === "cop" ? "es-CO" : "en-US")} {l.currency.toUpperCase()}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" data-testid={`paylink-copy-${l.id}`} className="rounded-full text-xs h-8"
                      onClick={() => { navigator.clipboard.writeText(l.url); toast.success("Link copiado"); }}>
                      Copiar link
                    </Button>
                    <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#2E5A3B] underline">Abrir</a>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="galeria">
            <p className="text-sm text-[#5C5C5C] mb-4">Creaciones guardadas por los niños en los juegos. Borra las que no quieras mostrar en /galeria.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-creations">
              {creations.map((c) => (
                <div key={c.id} data-testid={`admin-creation-${c.id}`} className="bg-white rounded-xl border border-black/10 overflow-hidden">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${c.image}`} alt={c.caption} className="w-full aspect-[9/10] object-cover" />
                  <div className="p-3 text-xs">
                    <p className="font-semibold text-[#132A1D]">{c.name} · {c.game}</p>
                    <p className="text-[#5C5C5C] truncate">{c.caption}</p>
                    <button data-testid={`admin-creation-delete-${c.id}`} className="mt-2 text-red-600 font-semibold"
                      onClick={async () => { await axios.delete(`${API}/admin/creations/${c.id}`, hdr()); setCreations(creations.filter((x) => x.id !== c.id)); toast.success("Creación eliminada"); }}>
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
              {creations.length === 0 && <p className="text-sm text-[#5C5C5C]">Aún no hay creaciones.</p>}
            </div>
          </TabsContent>

          <TabsContent value="contadores">
            <div className="max-w-sm space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Botellas recicladas</Label>
                <Input data-testid="admin-stats-bottles" type="number" value={stats.bottles_recycled}
                  onChange={(e) => setStats({ ...stats, bottles_recycled: e.target.value })}
                  className="bg-white border-black/10 h-10 rounded-lg" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Productos creados</Label>
                <Input data-testid="admin-stats-products" type="number" value={stats.products_created}
                  onChange={(e) => setStats({ ...stats, products_created: e.target.value })}
                  className="bg-white border-black/10 h-10 rounded-lg" />
              </div>
              <Button data-testid="admin-stats-save-btn" onClick={saveStats}
                className="rounded-full bg-[#2E5A3B] hover:bg-[#C86A4B] text-white transition-colors duration-300">
                Guardar contadores
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="admin-product-dialog" className="max-w-2xl bg-[#F7F5F0] max-h-[90vh] overflow-y-auto">
          <DialogTitle className="font-serif-d text-2xl">{editing?.id ? "Editar producto" : "Nuevo producto"}</DialogTitle>
          {editing && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                {ef("name_es", "Nombre (ES)")}
                {ef("name_en", "Nombre (EN)")}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Descripción (ES)</Label>
                <Textarea data-testid="admin-field-desc_es" rows={2} value={editing.desc_es ?? ""}
                  onChange={(e) => setEditing({ ...editing, desc_es: e.target.value })}
                  className="bg-white border-black/10 rounded-lg" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Descripción (EN)</Label>
                <Textarea data-testid="admin-field-desc_en" rows={2} value={editing.desc_en ?? ""}
                  onChange={(e) => setEditing({ ...editing, desc_en: e.target.value })}
                  className="bg-white border-black/10 rounded-lg" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Categoría</Label>
                  <select data-testid="admin-field-category" value={editing.category}
                    onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                    className="w-full bg-white border border-black/10 h-10 rounded-lg px-3 text-sm">
                    <option value="kits">Kits educativos</option>
                    <option value="figuras">Figuras personalizadas</option>
                    <option value="hogar">Hogar y oficina</option>
                    <option value="social">Línea social</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Subcategoría (kits)</Label>
                  <select data-testid="admin-field-subcategory" value={editing.subcategory ?? ""}
                    onChange={(e) => setEditing({ ...editing, subcategory: e.target.value })}
                    className="w-full bg-white border border-black/10 h-10 rounded-lg px-3 text-sm">
                    <option value="">—</option>
                    <option value="voladores">Voladores</option>
                    <option value="reptadores">Reptadores</option>
                    <option value="rodadores">Rodadores</option>
                    <option value="saltadores">Saltadores</option>
                    <option value="motricidad">Motricidad fina</option>
                  </select>
                </div>
                {ef("material", "Material")}
                {ef("colors", "Colores (separados por coma)")}
                {ef("price_cop", "Precio COP", "number")}
                {ef("price_usd", "Precio USD", "number")}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Foto del producto</Label>
                <div className="flex items-center gap-3">
                  {editing.image && (
                    <img src={editing.image} alt="" className="w-16 h-16 rounded-lg object-contain bg-[#EFECE6] border border-black/5" />
                  )}
                  <Button type="button" variant="outline" data-testid="admin-upload-image-btn" disabled={uploading}
                    onClick={() => imgFileRef.current?.click()} className="rounded-full h-10">
                    {uploading ? <><Loader2 className="animate-spin mr-2" size={15} />Subiendo...</> : <><Upload size={15} className="mr-2" />Subir foto</>}
                  </Button>
                  <input ref={imgFileRef} type="file" accept="image/*" className="hidden"
                    data-testid="admin-upload-image-input" onChange={uploadImage} />
                </div>
              </div>
              {ef("image", "o pega una URL de imagen")}
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <Switch data-testid="admin-field-featured" checked={!!editing.featured} onCheckedChange={(v) => setEditing({ ...editing, featured: v })} /> Destacado
                </label>
                <label className="flex items-center gap-2 text-sm font-medium">
                  <Switch data-testid="admin-field-active" checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} /> Activo
                </label>
                <label className="flex items-center gap-2 text-sm font-medium">
                  <Switch data-testid="admin-field-purchasable" checked={!!editing.purchasable} onCheckedChange={(v) => setEditing({ ...editing, purchasable: v })} /> Comprable
                </label>
              </div>
              <Button data-testid="admin-save-product-btn" onClick={saveProduct}
                className="w-full rounded-full bg-[#2E5A3B] hover:bg-[#C86A4B] text-white h-11 transition-colors duration-300">
                Guardar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!editingReview} onOpenChange={(o) => !o && setEditingReview(null)}>
        <DialogContent data-testid="admin-review-dialog" className="max-w-lg bg-[#F7F5F0]">
          <DialogTitle className="font-serif-d text-2xl">{editingReview?.id ? "Editar reseña" : "Nueva reseña"}</DialogTitle>
          {editingReview && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Nombre</Label>
                  <Input data-testid="admin-review-name" value={editingReview.name}
                    onChange={(e) => setEditingReview({ ...editingReview, name: e.target.value })}
                    className="bg-white border-black/10 h-10 rounded-lg" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Usuario de Instagram</Label>
                  <Input data-testid="admin-review-handle" placeholder="@usuario" value={editingReview.handle}
                    onChange={(e) => setEditingReview({ ...editingReview, handle: e.target.value })}
                    className="bg-white border-black/10 h-10 rounded-lg" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Comentario (ES)</Label>
                <Textarea data-testid="admin-review-text-es" rows={3} value={editingReview.text_es}
                  onChange={(e) => setEditingReview({ ...editingReview, text_es: e.target.value })}
                  className="bg-white border-black/10 rounded-lg" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Comentario (EN, opcional)</Label>
                <Textarea data-testid="admin-review-text-en" rows={3} value={editingReview.text_en}
                  onChange={(e) => setEditingReview({ ...editingReview, text_en: e.target.value })}
                  className="bg-white border-black/10 rounded-lg" />
              </div>
              <label className="flex items-center gap-2 text-sm font-medium">
                <Switch data-testid="admin-review-active" checked={!!editingReview.active}
                  onCheckedChange={(v) => setEditingReview({ ...editingReview, active: v })} /> Visible en el sitio
              </label>
              <Button data-testid="admin-save-review-btn" onClick={saveReview}
                className="w-full rounded-full bg-[#2E5A3B] hover:bg-[#C86A4B] text-white h-11 transition-colors duration-300">
                Guardar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
